/**
 * ============================================================================
 * LINK GENERATOR COMPONENT - 100% Ready
 * ============================================================================
 * 
 * Generate and manage transaction links
 */

import React, { useState } from 'react';
import { useLinks } from '../../hooks/useLinks';
import { useToast } from '../../providers';
import { useI18n } from '../../providers';
import Card from '../common/Card';
import Button from '../common/Button';
import Input from '../common/Input';
import Spinner from '../common/Spinner';
import { useCopyToClipboard } from '../../hooks/useCopyToClipboard';

/**
 * Link Generator Component
 * @param {Object} props
 * @param {string} props.transactionId - Transaction ID
 * @param {Function} props.onLinkGenerated - Callback when link is generated
 * @returns {JSX.Element}
 */
const LinkGenerator = ({ transactionId, onLinkGenerated }) => {
  const { generateShortLink, regenerateShortLink, loading } = useLinks();
  const { showSuccess, showError } = useToast();
  const { language } = useI18n();
  const { copy, copied } = useCopyToClipboard();
  const [shortLink, setShortLink] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (!transactionId) {
      showError(language === 'fr' ? 'ID de transaction requis' : 'Transaction ID required');
      return;
    }

    try {
      setIsGenerating(true);
      const linkData = await generateShortLink(transactionId);
      const link = linkData.shortUrl || linkData.url || linkData;
      setShortLink(link);
      showSuccess(
        language === 'fr' ? 'Lien généré avec succès!' : 'Link generated successfully!'
      );
      if (onLinkGenerated) {
        onLinkGenerated(link);
      }
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de génération du lien' : 'Failed to generate link')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRegenerate = async () => {
    if (!transactionId) {
      showError(language === 'fr' ? 'ID de transaction requis' : 'Transaction ID required');
      return;
    }

    try {
      setIsGenerating(true);
      const linkData = await regenerateShortLink(transactionId);
      const link = linkData.shortUrl || linkData.url || linkData;
      setShortLink(link);
      showSuccess(
        language === 'fr' ? 'Lien régénéré avec succès!' : 'Link regenerated successfully!'
      );
      if (onLinkGenerated) {
        onLinkGenerated(link);
      }
    } catch (error) {
      showError(
        error.message || (language === 'fr' ? 'Échec de régénération du lien' : 'Failed to regenerate link')
      );
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = async () => {
    if (!shortLink) {
      showError(language === 'fr' ? 'Aucun lien à copier' : 'No link to copy');
      return;
    }

    try {
      await copy(shortLink);
      showSuccess(language === 'fr' ? 'Lien copié!' : 'Link copied!');
    } catch (error) {
      showError(language === 'fr' ? 'Échec de la copie' : 'Failed to copy');
    }
  };

  return (
    <Card variant="glass" className="animate-fadeInUp">
      <div className="space-y-4">
        <h3 className="text-lg font-black text-gray-900 dark:text-white flex items-center gap-2">
          <span className="text-2xl">🔗</span>
          {language === 'fr' ? 'Générateur de lien' : 'Link Generator'}
        </h3>

        {/* Link Display */}
        {shortLink && (
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              {language === 'fr' ? 'Lien généré:' : 'Generated Link:'}
            </label>
            <div className="flex gap-2">
              <Input
                type="text"
                value={shortLink}
                readOnly
                className="flex-1 font-mono text-sm"
              />
              <Button
                variant={copied ? 'success' : 'outline'}
                onClick={handleCopy}
                disabled={!shortLink}
                className="flex-shrink-0"
              >
                {copied ? (language === 'fr' ? '✓' : '✓') : '📋'}
              </Button>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex gap-2">
          {!shortLink ? (
            <Button
              variant="gradient"
              fullWidth
              onClick={handleGenerate}
              loading={isGenerating || loading}
              className="shadow-xl hover:shadow-2xl transition-all"
            >
              {language === 'fr' ? 'Générer le lien' : 'Generate Link'} →
            </Button>
          ) : (
            <>
              <Button
                variant="outline"
                fullWidth
                onClick={handleRegenerate}
                loading={isGenerating || loading}
              >
                {language === 'fr' ? 'Régénérer' : 'Regenerate'}
              </Button>
              <Button
                variant="gradient"
                fullWidth
                onClick={handleCopy}
                disabled={!shortLink}
                className="shadow-xl hover:shadow-2xl transition-all"
              >
                {copied
                  ? language === 'fr'
                    ? '✓ Copié'
                    : '✓ Copied'
                  : language === 'fr'
                  ? 'Copier'
                  : 'Copy'}
              </Button>
            </>
          )}
        </div>

        {/* Info */}
        <Card variant="outlined" className="bg-blue-50 dark:bg-blue-900/20 border-blue-200 dark:border-blue-800 p-3">
          <p className="text-xs text-blue-700 dark:text-blue-300">
            {language === 'fr'
              ? '💡 Le lien permet à votre contrepartie d\'accéder à la transaction et de procéder au paiement'
              : '💡 The link allows your counterparty to access the transaction and proceed with payment'}
          </p>
        </Card>
      </div>
    </Card>
  );
};

export default LinkGenerator;
